import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  RefreshCw, AlertTriangle, Target, Layers, Coins, ChevronDown, ChevronRight,
  Info, ShieldAlert, ListChecks
} from 'lucide-react';

/**
 * BlackSpade · Dönüşüm & ROAS Denetimi — canlı Google Ads, DB'ye dokunmaz.
 *
 * Backend: GET /api/blackspade-conversions/audit?days=30
 *
 * SAYFANIN TEK DERDİ: hesapta iki ayrı satın alma dönüşümü aynı anda birincil,
 * yani panelin gösterdiği ciro aynı siparişleri iki kez sayıyor. Bu sayfa o iki
 * dönüşümü BİRLEŞTİRMEZ — yan yana, her biri kendi ROAS'ıyla gösterir ve
 * hangisinin referans alınacağını kullanıcıya bırakır. Çünkü hangisinin gerçek
 * sipariş adedine oturduğu henüz doğrulanmadı; sayfanın işi karar vermek değil,
 * kararı verecek veriyi tek ekranda göstermek.
 *
 * Sadece BlackSpade brand'inde görünür (App.js allowBrandIds + Sidebar
 * allowBrandIds + route'un kendi brand kontrolü). Bilerek yalnız açık tema.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const RANGES = [{ d: 7, l: '7 gün' }, { d: 30, l: '30 gün' }, { d: 90, l: '90 gün' }, { d: 180, l: '180 gün' }];

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const nf1 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nf2 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const tl = (n) => `${Math.round(Number(n || 0)).toLocaleString('tr-TR')} ₺`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}M ₺`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B ₺`;
  return `${Math.round(v)} ₺`;
};

const STRATEJI_TR = {
  MAXIMIZE_CONVERSION_VALUE: 'Maks. dönüşüm değeri',
  MAXIMIZE_CONVERSIONS: 'Maks. dönüşüm',
  TARGET_SPEND: 'Maks. tıklama',
  TARGET_CPV: 'Hedef CPV',
  TARGET_ROAS: 'Hedef ROAS',
  TARGET_CPA: 'Hedef CPA',
  MANUAL_CPC: 'Manuel TBM',
  TARGET_IMPRESSION_SHARE: 'Hedef gös. payı',
};
const KANAL_TR = {
  SEARCH: 'Arama', PERFORMANCE_MAX: 'PMax', DISPLAY: 'Görüntülü', SHOPPING: 'Alışveriş',
  VIDEO: 'Video', DEMAND_GEN: 'Demand Gen', MULTI_CHANNEL: 'Uygulama',
};
const KATEGORI_TR = {
  PURCHASE: 'Satın alma', BEGIN_CHECKOUT: 'Ödeme başlatma', ADD_TO_CART: 'Sepete ekleme',
  PAGE_VIEW: 'Sayfa görüntüleme', DOWNLOAD: 'Uygulama yükleme', SUBSCRIBE_PAID: 'Abonelik',
  STORE_VISIT: 'Mağaza ziyareti', ENGAGEMENT: 'Etkileşim', CONTACT: 'İletişim',
  GET_DIRECTIONS: 'Yol tarifi', SIGNUP: 'Kayıt', DEFAULT: 'Diğer', UNKNOWN: 'Bilinmiyor',
};

/* --------------------------------------------------------------- parçacıklar */

function Kpi({ label, value, sub, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-900', rose: 'text-rose-700', amber: 'text-amber-700', emerald: 'text-emerald-700',
  };
  return (
    <div className="border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-[24px] font-bold leading-none tabular-nums ${tones[tone]}`}>{value}</div>
      {sub ? <div className="mt-1 text-[12px] leading-snug text-slate-500">{sub}</div> : null}
    </div>
  );
}

function Section({ icon: Icon, eyebrow, title, note, children }) {
  return (
    <section className="mt-10">
      <div className="flex items-start gap-2.5">
        {Icon ? <Icon size={18} className="mt-[3px] shrink-0 text-slate-700" /> : null}
        <div>
          <h2 className="text-[12px] font-bold uppercase tracking-wider text-slate-600">{eyebrow}</h2>
          <p className="mt-1 text-[20px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
          {note ? <p className="mt-1.5 max-w-[92ch] text-[13px] leading-relaxed text-slate-500">{note}</p> : null}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Rozet({ tone = 'slate', children, title }) {
  const t = {
    slate: 'bg-slate-100 text-slate-600',
    rose: 'bg-rose-50 text-rose-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  }[tone];
  return (
    <span title={title} className={`inline-block whitespace-nowrap px-1.5 py-[2px] text-[10px] font-semibold uppercase tracking-wide ${t}`}>
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------- sayfa */

export default function BlackspadeConversionAudit() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [refId, setRefId] = useState(null);   // referans satın alma dönüşümü
  const [open, setOpen] = useState({});       // kampanya kırılımı açık mı

  const load = useCallback(async (refresh) => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/blackspade-conversions/audit?days=${days}${refresh ? '&refresh=1' : ''}`,
        { credentials: 'include', headers }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || json.error || 'Veri yüklenemedi');
      setData(json);
      setRefId((prev) => {
        const ids = (json.purchaseActions || []).map((p) => p.id);
        return prev && ids.includes(prev) ? prev : (ids[0] || null);
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, days]);

  useEffect(() => { load(false); }, [load]);

  const purchases = data?.purchaseActions || [];
  const campaigns = data?.campaigns || [];
  const totals = data?.totals || {};
  const ref = purchases.find((p) => p.id === refId) || purchases[0] || null;

  const refTotal = useMemo(
    () => campaigns.reduce((a, c) => a + (c.purchases.find((p) => p.id === ref?.id)?.value || 0), 0),
    [campaigns, ref]
  );
  const panelRoas = totals.cost > 0 ? totals.conversionsValue / totals.cost : 0;
  const gercekRoas = totals.cost > 0 ? refTotal / totals.cost : 0;
  const cift = purchases.length > 1;
  const fazla = totals.conversionsValue - refTotal;

  const primaryActions = (data?.conversionActions || []).filter((a) => a.primary);
  const secondaryActive = (data?.conversionActions || []).filter((a) => !a.primary && a.conversions > 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1500px] px-6 py-8">

        {/* başlık */}
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-5">
          <div>
            <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
              BlackSpade · Google Ads
            </div>
            <h1 className="mt-1 text-[28px] font-bold leading-tight tracking-tight text-slate-900">
              Dönüşüm &amp; ROAS Denetimi
            </h1>
            <p className="mt-1.5 max-w-[92ch] text-[13px] leading-relaxed text-slate-500">
              Tamamı canlı Google Ads API'sinden çekilir, hiçbir veritabanı tablosuna dokunmaz.
              {data?.range ? ` Aralık: ${data.range.start} – ${data.range.end}.` : ''}
              {data?.customerId ? ` Hesap: ${data.customerId}${data.accountName ? ` (${data.accountName})` : ''}.` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-slate-300 bg-white">
              {RANGES.map((r) => (
                <button
                  key={r.d}
                  onClick={() => setDays(r.d)}
                  className={`px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                    days === r.d ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >{r.l}</button>
              ))}
            </div>
            <button
              onClick={() => load(true)}
              disabled={loading}
              className="flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Yenile
            </button>
          </div>
        </div>

        {data?.cached ? (
          <p className="mt-2 text-[11px] text-slate-400">
            Önbellekten ({Math.round((data.cacheAgeSec || 0) / 60)} dk önce çekildi) · yeniden çekmek için Yenile
          </p>
        ) : null}

        {loading && !data ? (
          <div className="mt-16 text-center text-[13px] text-slate-500">Google Ads'ten canlı çekiliyor…</div>
        ) : null}

        {!brandId ? (
          <div className="mt-8 border border-amber-300 bg-amber-50 p-4 text-[13px] text-amber-800">
            Marka seçili değil. Üstteki marka seçicisinden BlackSpade'i seçin.
          </div>
        ) : null}

        {data ? (
          <>
            {/* çift sayım uyarısı */}
            {cift ? (
              <div className="mt-6 border-l-4 border-rose-500 bg-rose-50 p-4">
                <div className="flex items-start gap-2.5">
                  <ShieldAlert size={18} className="mt-[2px] shrink-0 text-rose-600" />
                  <div>
                    <div className="text-[13px] font-bold text-rose-900">
                      {purchases.length} ayrı satın alma dönüşümü aynı anda birincil — ciro çift sayılıyor
                    </div>
                    <p className="mt-1 max-w-[100ch] text-[13px] leading-relaxed text-rose-800">
                      {purchases.map((p) => `“${p.name}”`).join(' ve ')} dönüşümlerinin hepsi
                      “Dönüşümler” sütununa giriyor. Panelin gösterdiği{' '}
                      <strong>{tl(totals.conversionsValue)}</strong> ciro, referans aldığın{' '}
                      <strong>{tl(refTotal)}</strong>'nin{' '}
                      <strong>{nf2(refTotal > 0 ? totals.conversionsValue / refTotal : 0)} katı</strong> —
                      aradaki <strong>{tl(fazla)}</strong> fiziksel olarak var olmayan ciro.
                      Akıllı Teklif de bu şişmiş veriye göre optimize ediyor.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* referans seçici */}
            {cift ? (
              <div className="mt-5 border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Referans satın alma dönüşümü
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {purchases.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setRefId(p.id)}
                        className={`border px-2.5 py-1 text-[12px] font-semibold transition-colors ${
                          p.id === refId
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {p.name} <span className="opacity-70">· {nf1(p.conversions)} · {tlShort(p.value)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <p className="mt-2 max-w-[100ch] text-[12px] leading-relaxed text-slate-500">
                  <Info size={12} className="mr-1 inline align-[-1px]" />
                  Hangisinin gerçek sipariş adedine oturduğu <strong>henüz doğrulanmadı</strong>. Seçtiğin
                  dönüşüm aşağıdaki “gerçekçi” sütunlarını besler; diğeri tabloda ayrı sütun olarak kalır.
                  Karar vermeden önce e-ticaret tarafındaki gerçek sipariş adediyle karşılaştır.
                </p>
              </div>
            ) : null}

            {/* KPI */}
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <Kpi label="Maliyet" value={tlShort(totals.cost)} sub={`${nf(totals.clicks)} tık · ${tl(totals.clicks ? totals.cost / totals.clicks : 0)} TBM`} />
              <Kpi label="Panelde ciro" value={tlShort(totals.conversionsValue)} sub="tüm birincil dönüşümler" tone="rose" />
              <Kpi label="Gerçekçi ciro" value={tlShort(refTotal)} sub={ref ? ref.name : '—'} tone="emerald" />
              <Kpi label="Panel ROAS" value={nf2(panelRoas)} sub="şişmiş" tone="rose" />
              <Kpi label="Gerçekçi ROAS" value={nf2(gercekRoas)} sub={`panelin %${nf(panelRoas ? (gercekRoas / panelRoas) * 100 : 0)}'i`} tone="emerald" />
              <Kpi label="Var olmayan ciro" value={tlShort(fazla)} sub={`${nf1(totals.conversions)} “dönüşüm” raporlanıyor`} tone="amber" />
            </div>

            {/* ana tablo */}
            <Section
              icon={Coins}
              eyebrow="Kampanyalar"
              title="Strateji, hedef ve her satın alma dönüşümünün kendi ROAS'ı"
              note="Harcamaya göre sıralı. Her satın alma dönüşümü kendi adedi, değeri ve ROAS'ıyla ayrı sütunda — hiçbiri toplanmadı. En sağdaki panel sütunu Google Ads arayüzünün gösterdiği rakam. Satıra tıklayarak o kampanyanın tam dönüşüm kırılımını açabilirsin."
            >
              <div className="overflow-x-auto border border-slate-200 bg-white">
                <table className="w-full min-w-[1400px] border-collapse text-[12px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left">
                      <th className="px-3 py-2 font-semibold text-slate-600">Kampanya</th>
                      <th className="px-3 py-2 font-semibold text-slate-600">Strateji</th>
                      <th className="px-3 py-2 font-semibold text-slate-600">Hedef</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600">Bütçe/gün</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600">Maliyet</th>
                      {purchases.map((p) => (
                        <th key={p.id} colSpan={3} className={`border-l border-slate-200 px-3 py-2 text-center font-semibold ${p.id === refId ? 'bg-emerald-50 text-emerald-800' : 'text-slate-600'}`}>
                          {p.name}
                        </th>
                      ))}
                      <th className="border-l border-slate-200 px-3 py-2 text-right font-semibold text-rose-700">Panel ciro</th>
                      <th className="px-3 py-2 text-right font-semibold text-rose-700">Panel ROAS</th>
                    </tr>
                    <tr className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                      <th colSpan={5} />
                      {purchases.map((p) => (
                        <React.Fragment key={p.id}>
                          <th className={`border-l border-slate-200 px-3 py-1 text-right font-semibold ${p.id === refId ? 'bg-emerald-50' : ''}`}>adet</th>
                          <th className={`px-3 py-1 text-right font-semibold ${p.id === refId ? 'bg-emerald-50' : ''}`}>değer</th>
                          <th className={`px-3 py-1 text-right font-semibold ${p.id === refId ? 'bg-emerald-50' : ''}`}>ROAS</th>
                        </React.Fragment>
                      ))}
                      <th colSpan={2} className="border-l border-slate-200" />
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c) => {
                      const acik = !!open[c.id];
                      return (
                        <React.Fragment key={c.id}>
                          <tr
                            onClick={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))}
                            className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                          >
                            <td className="px-3 py-2">
                              <div className="flex items-start gap-1.5">
                                {acik ? <ChevronDown size={13} className="mt-[2px] shrink-0 text-slate-400" />
                                      : <ChevronRight size={13} className="mt-[2px] shrink-0 text-slate-400" />}
                                <div>
                                  <div className="font-semibold text-slate-900">
                                    {c.name.replace(/^Boo_Blackspade_/, '')}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                    <Rozet>{KANAL_TR[c.channelType] || c.channelType}</Rozet>
                                    {c.status !== 'ENABLED' ? <Rozet tone="slate">{c.status}</Rozet> : null}
                                    {c.experimentType && c.experimentType !== 'BASE'
                                      ? <Rozet tone="indigo" title="Google'ın deneme mekanizmasının otomatik kopyaladığı kampanya">deneme</Rozet> : null}
                                    {c.goalLevel === 'campaign'
                                      ? <Rozet tone="amber" title={c.goals.join(', ')}>kampanya özel hedef</Rozet> : null}
                                    {c.goalLevel === 'none'
                                      ? <Rozet tone="rose" title="API'de biddable hedef görünmüyor">hedef yok</Rozet> : null}
                                    {c.budgetShared ? <Rozet tone="slate">paylaşımlı bütçe</Rozet> : null}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-slate-600">{STRATEJI_TR[c.biddingStrategy] || c.biddingStrategy || '—'}</td>
                            <td className="px-3 py-2">
                              {c.targetRoas ? <span className="font-bold text-indigo-700">tROAS %{nf(c.targetRoas * 100)}</span>
                                : c.targetCpa ? <span className="font-bold text-indigo-700">tCPA {tl(c.targetCpa)}</span>
                                : <span className="text-slate-400">—</span>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">{c.budgetDaily ? tl(c.budgetDaily) : '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">{tl(c.cost)}</td>
                            {c.purchases.map((p) => {
                              const isRef = p.id === refId;
                              return (
                                <React.Fragment key={p.id}>
                                  <td className={`border-l border-slate-200 px-3 py-2 text-right tabular-nums text-slate-600 ${isRef ? 'bg-emerald-50/50' : ''}`}>{nf1(p.conversions)}</td>
                                  <td className={`px-3 py-2 text-right tabular-nums text-slate-700 ${isRef ? 'bg-emerald-50/50' : ''}`}>{tl(p.value)}</td>
                                  <td className={`px-3 py-2 text-right font-bold tabular-nums ${isRef ? 'bg-emerald-50/50 text-emerald-800' : 'text-slate-500'}`}>{nf2(p.roas)}</td>
                                </React.Fragment>
                              );
                            })}
                            <td className="border-l border-slate-200 px-3 py-2 text-right tabular-nums text-rose-700">{tl(c.conversionsValue)}</td>
                            <td className="px-3 py-2 text-right font-bold tabular-nums text-rose-700">{nf2(c.panelRoas)}</td>
                          </tr>

                          {acik ? (
                            <tr className="border-b border-slate-200 bg-slate-50/70">
                              <td colSpan={6 + purchases.length * 3} className="px-3 py-3">
                                <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                  Tam dönüşüm kırılımı · {c.name}
                                </div>
                                {c.goals?.length ? (
                                  <div className="mb-2 text-[12px] text-slate-600">
                                    Teklife giren hedefler:{' '}
                                    <span className="font-semibold text-slate-800">{c.goals.join(' · ')}</span>
                                    {c.goalLevel === 'campaign'
                                      ? <span className="ml-1 text-amber-700">(hesap varsayılanı ezilmiş)</span> : null}
                                  </div>
                                ) : null}
                                <div className="overflow-x-auto">
                                  <table className="w-full min-w-[720px] border-collapse text-[12px]">
                                    <thead>
                                      <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wide text-slate-400">
                                        <th className="px-2 py-1 font-semibold">Dönüşüm</th>
                                        <th className="px-2 py-1 font-semibold">Kategori</th>
                                        <th className="px-2 py-1 font-semibold">Sayılıyor mu</th>
                                        <th className="px-2 py-1 text-right font-semibold">Adet</th>
                                        <th className="px-2 py-1 text-right font-semibold">Değer</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {c.breakdown.map((b) => (
                                        <tr key={b.id} className="border-b border-slate-100">
                                          <td className="px-2 py-1 text-slate-700">{b.name}</td>
                                          <td className="px-2 py-1 text-slate-500">{KATEGORI_TR[b.category] || b.category || '—'}</td>
                                          <td className="px-2 py-1">
                                            {b.primary ? <Rozet tone="rose">birincil</Rozet> : <Rozet>ikincil</Rozet>}
                                          </td>
                                          <td className="px-2 py-1 text-right tabular-nums text-slate-600">{nf1(b.conversions)}</td>
                                          <td className="px-2 py-1 text-right tabular-nums text-slate-600">{b.value ? tl(b.value) : '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {Math.abs(c.purchaseSum - c.conversionsValue) > 10 ? (
                                  <p className="mt-2 text-[12px] text-slate-500">
                                    Satın alma toplamı {tl(c.purchaseSum)}, panel {tl(c.conversionsValue)} —
                                    aradaki {tl(Math.abs(c.purchaseSum - c.conversionsValue))} fark, bu kanalda
                                    “tüm dönüşümler” ile “dönüşümler” metriklerinin ayrışmasından geliyor
                                    (Demand Gen'de etkileşimli görüntüleme dönüşümleri).
                                  </p>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                      <td className="px-3 py-2 text-slate-900">TOPLAM · {campaigns.length} kampanya</td>
                      <td colSpan={3} />
                      <td className="px-3 py-2 text-right tabular-nums text-slate-900">{tl(totals.cost)}</td>
                      {purchases.map((p) => {
                        const adet = campaigns.reduce((a, c) => a + (c.purchases.find((x) => x.id === p.id)?.conversions || 0), 0);
                        const deger = campaigns.reduce((a, c) => a + (c.purchases.find((x) => x.id === p.id)?.value || 0), 0);
                        const isRef = p.id === refId;
                        return (
                          <React.Fragment key={p.id}>
                            <td className={`border-l border-slate-200 px-3 py-2 text-right tabular-nums ${isRef ? 'bg-emerald-50' : ''}`}>{nf1(adet)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${isRef ? 'bg-emerald-50' : ''}`}>{tl(deger)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${isRef ? 'bg-emerald-50 text-emerald-800' : 'text-slate-600'}`}>
                              {nf2(totals.cost ? deger / totals.cost : 0)}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className="border-l border-slate-200 px-3 py-2 text-right tabular-nums text-rose-700">{tl(totals.conversionsValue)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700">{nf2(panelRoas)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Section>

            {/* satın alma dönüşümlerinin karşılaştırması */}
            {cift ? (
              <Section
                icon={Layers}
                eyebrow="Karşılaştırma"
                title="İki satın alma dönüşümü aynı siparişleri mi ölçüyor?"
                note="Ortalama sepet tutarı birbirine yakınsa ama adetler farklıysa, ikisi aynı siparişleri ölçüyor demektir — biri daha az yakalıyor. Sepet tutarları ayrışıyorsa farklı işlemleri sayıyor olabilirler. Yakalama oranı referans dönüşüme göre hesaplanır."
              >
                <div className="overflow-x-auto border border-slate-200 bg-white">
                  <table className="w-full min-w-[900px] border-collapse text-[12px]">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-left">
                        <th className="px-3 py-2 font-semibold text-slate-600">Kampanya</th>
                        {purchases.map((p) => (
                          <th key={p.id} className="px-3 py-2 text-right font-semibold text-slate-600">
                            {p.name}<br /><span className="text-[10px] font-normal uppercase text-slate-400">ort. sepet</span>
                          </th>
                        ))}
                        <th className="px-3 py-2 text-right font-semibold text-slate-600">
                          Yakalama<br /><span className="text-[10px] font-normal uppercase text-slate-400">diğer / referans</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.filter((c) => (c.purchases.find((p) => p.id === refId)?.conversions || 0) > 0).map((c) => {
                        const r = c.purchases.find((p) => p.id === refId);
                        const other = c.purchases.filter((p) => p.id !== refId);
                        const kap = other.length && r?.conversions
                          ? other.reduce((a, o) => a + o.conversions, 0) / other.length / r.conversions : 0;
                        return (
                          <tr key={c.id} className="border-b border-slate-100">
                            <td className="px-3 py-2 text-slate-700">{c.name.replace(/^Boo_Blackspade_/, '')}</td>
                            {c.purchases.map((p) => (
                              <td key={p.id} className={`px-3 py-2 text-right tabular-nums ${p.id === refId ? 'bg-emerald-50/50 font-semibold text-slate-800' : 'text-slate-600'}`}>
                                {p.conversions > 0 ? tl(p.value / p.conversions) : '—'}
                              </td>
                            ))}
                            <td className={`px-3 py-2 text-right font-bold tabular-nums ${kap < 0.6 ? 'text-rose-700' : 'text-slate-700'}`}>
                              %{nf(kap * 100)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Section>
            ) : null}

            {/* birincil dönüşüm listesi */}
            <Section
              icon={ListChecks}
              eyebrow="Dönüşüm eylemleri"
              title={`Hesapta ${primaryActions.length} birincil dönüşüm var`}
              note={`Birincil olan her dönüşüm “Dönüşümler” sütununa girer ve Akıllı Teklif'e yön verir.${
                data.hiddenPrimaryCount ? ` Ayrıca ${data.hiddenPrimaryCount} adet gizli/pasif durumda birincil işaretli eski kayıt var — veri üretmiyorlar, listede gösterilmiyor.` : ''
              }`}
            >
              <div className="overflow-x-auto border border-slate-200 bg-white">
                <table className="w-full min-w-[820px] border-collapse text-[12px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left">
                      <th className="px-3 py-2 font-semibold text-slate-600">Dönüşüm</th>
                      <th className="px-3 py-2 font-semibold text-slate-600">Kategori</th>
                      <th className="px-3 py-2 font-semibold text-slate-600">Kaynak</th>
                      <th className="px-3 py-2 font-semibold text-slate-600">Durum</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600">Adet</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600">Değer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {primaryActions.map((a) => (
                      <tr key={a.id} className={`border-b border-slate-100 ${a.category === 'PURCHASE' && a.value > 0 ? 'bg-rose-50/40' : ''}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {a.name}
                          {a.id === refId ? <Rozet tone="emerald">referans</Rozet> : null}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{KATEGORI_TR[a.category] || a.category}</td>
                        <td className="px-3 py-2 text-slate-500">{a.origin === 'WEBSITE' ? 'Web' : a.origin === 'APP' ? 'Uygulama' : a.origin}</td>
                        <td className="px-3 py-2">{a.status === 'ENABLED' ? <Rozet tone="emerald">aktif</Rozet> : <Rozet>{a.status}</Rozet>}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{nf1(a.conversions)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{a.value ? tl(a.value) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {secondaryActive.length ? (
                <details className="mt-3 border border-slate-200 bg-white">
                  <summary className="cursor-pointer px-3 py-2 text-[12px] font-semibold text-slate-600">
                    İkincil ama veri üreten {secondaryActive.length} dönüşüm (teklife girmez, sütuna yazılmaz)
                  </summary>
                  <table className="w-full border-collapse text-[12px]">
                    <tbody>
                      {secondaryActive.map((a) => (
                        <tr key={a.id} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 text-slate-600">{a.name}</td>
                          <td className="px-3 py-1.5 text-slate-400">{KATEGORI_TR[a.category] || a.category}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{nf1(a.conversions)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{a.value ? tl(a.value) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ) : null}
            </Section>

            {/* hedef seçimi */}
            <Section
              icon={Target}
              eyebrow="Hedef seçimi"
              title="Hangi kampanya hesap varsayılanını eziyor?"
              note="Google Ads'te dönüşüm hedefleri hesap seviyesinde tanımlanır ama kampanya tek tek ezebilir. Ezilmiş kampanyalarda sepete ekleme gibi huni ortası dönüşümler “Dönüşümler” sütununa girip teklife yön veriyor olabilir."
            >
              <div className="border border-slate-200 bg-white p-4">
                <div className="text-[12px] text-slate-600">
                  <span className="font-bold uppercase tracking-wider text-slate-500">Hesap varsayılanı:</span>{' '}
                  {(data.accountGoals || []).join(' · ') || '—'}
                </div>
                <div className="mt-3 space-y-2">
                  {campaigns.filter((c) => c.goalLevel !== 'account').map((c) => (
                    <div key={c.id} className="flex flex-wrap items-baseline gap-2 border-t border-slate-100 pt-2 text-[12px]">
                      <span className="font-semibold text-slate-800">{c.name.replace(/^Boo_Blackspade_/, '')}</span>
                      {c.goalLevel === 'none'
                        ? <Rozet tone="rose">API'de biddable hedef yok</Rozet>
                        : <span className="text-slate-600">{c.goals.join(' · ')}</span>}
                    </div>
                  ))}
                  {campaigns.every((c) => c.goalLevel === 'account')
                    ? <p className="text-[12px] text-slate-500">Tüm kampanyalar hesap varsayılanını kullanıyor.</p> : null}
                </div>
              </div>
            </Section>

            {/* okuma notu */}
            <div className="mt-10 border-l-4 border-slate-300 bg-white p-4">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={16} className="mt-[2px] shrink-0 text-amber-600" />
                <div className="max-w-[100ch] text-[13px] leading-relaxed text-slate-600">
                  <strong className="text-slate-800">Bu sayfa bir karar vermez, kararı hazırlar.</strong>{' '}
                  Çift sayımı düzeltmek için birincil satın alma dönüşümlerinden yalnız biri kalmalı. Ama
                  düzeltme uygulandığı anda “Dönüşüm değeri” sütunu bir gecede düşer ve hedef ROAS ile çalışan
                  kampanyalar “hedefimi tutturamıyorum” deyip trafiği kısar. Hedefi olan kampanyalarda hedefi de
                  aynı oranda düşürmek gerekir — bu bir gevşetme değil, aynı yerde kalmak için yapılan
                  matematiksel düzeltme. Akıllı Teklif ayrıca 1-2 hafta yeniden öğrenme yaşar.
                </div>
              </div>
            </div>

            <p className="mt-8 border-t border-slate-200 pt-4 text-[11px] text-slate-400">
              Canlı Google Ads API · {data.fetchedAt ? new Date(data.fetchedAt).toLocaleString('tr-TR') : ''} ·
              Google Ads verisi bir gün gecikmeli oturur, bitiş tarihi dün.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
